import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useMe } from '../lib/me';
import { contractorSignedIn } from '../lib/contractorAuth';
import '../components/Assets.css';

/**
 * What a scanned label opens: /a/<tag>.
 *
 * Somebody points a phone camera at the sticker on a jammed printer, and
 * the phone opens this address. Where it goes from here depends on who they
 * are, which only the server knows:
 *
 *   somebody the register lets in   the device's page
 *   any other member of staff       Create Ticket, with the device chosen
 *   a retired device                a sentence saying so
 *   nobody signed in yet            sign in first, then straight back here
 *
 * Remembering where they were going matters: without it, signing in drops
 * them on My tickets and the label they scanned is forgotten.
 */
export const AFTER_SIGN_IN = 'jarvis-after-sign-in';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function TagLanding() {
  const { tag } = useParams();
  const me = useMe();
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!me?.signedIn) return;
    axios.get(`${API_BASE}/assets/by-tag/${encodeURIComponent(tag)}`)
      .then((r) => setResult(r.data))
      .catch((e) => setResult({ error: e.response?.status === 404
        ? `No device in the register has the label ${tag}.`
        : e.response?.data?.error || 'Could not look that label up.' }));
  }, [me, tag]);

  if (me === null || me === undefined) return <div className="container"><p className="as-quiet">Opening…</p></div>;

  if (!me.signedIn) {
    if (contractorSignedIn()) {
      return (
        <div className="container">
          <p className="as-note">This label is for NCMB staff. The device’s details are on the ticket you were sent.</p>
        </div>
      );
    }
    try { sessionStorage.setItem(AFTER_SIGN_IN, `/a/${encodeURIComponent(tag)}`); } catch { /* private window */ }
    return <Navigate to="/login" replace />;
  }

  if (!result) return <div className="container"><p className="as-quiet">{`Looking up ${tag}…`}</p></div>;
  if (result.go === 'asset') return <Navigate to={`/assets/${result.id}?scanned=1`} replace />;
  if (result.go === 'report') return <Navigate to={`/create?asset=${result.id}`} replace />;

  return (
    <div className="container">
      <div className="page-header"><h1 className="page-title">{tag}</h1></div>
      <p className={result.error ? 'as-error' : 'as-note'} role="status">
        {result.error || (result.go === 'off'
          ? `${result.asset_tag}${result.name ? ` (${result.name})` : ''} is in the register, but the register is not in use yet. Raise a ticket and say which device it is.`
          : null) || `${result.asset_tag}${result.name ? ` (${result.name})` : ''} has been retired, so a ticket cannot be raised about it. Raise one without a device if something still needs doing.`}
      </p>
      <Link className="btn btn-primary" to="/create">Raise a ticket</Link>
    </div>
  );
}
